import { CmsFontColor } from "@/components/tiptap/extensions/FontColor";
import { CmsFontFamily } from "@/components/tiptap/extensions/FontFamily";
import { Underline } from "@/components/tiptap/extensions/Underline";
import { FontSize } from "@/components/tiptap/TextStyle.tsx";
import {
  type ToolbarPreset,
} from "@/components/tiptap/toolbar-preset";
import { CMS_FONT_SIZES_PX } from "@/lib/cms/cms-theme.constants";
import { Label } from "@/components/ui/label.tsx";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip.tsx";
import { cn } from "@/lib/utils";
import { Link } from "@tiptap/extension-link";
import Paragraph from "@tiptap/extension-paragraph";
import { TextAlign } from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { HelpCircleIcon } from "lucide-react";
import React, { useEffect } from "react";
import { CustomHeading } from "./CustmHeading";
import { Indent } from "./Indent";
import ReadMore from "./extensions/ReadMore";
import ToolBar from "./ToolBar";

const SPACING_PARAGRAPH_DEFAULT = "spacing-paragraph-4";
const SPACING_PARAGRAPH_VALID = /^spacing-paragraph-(2|4|6)$/;

const CustomParagraph = Paragraph.extend({
  addAttributes() {
    return {
      class: {
        default: SPACING_PARAGRAPH_DEFAULT,
        parseHTML: (element) => {
          const cls = element.getAttribute("class") || "";
          const match = cls.split(/\s+/).find((c) => SPACING_PARAGRAPH_VALID.test(c));
          return match ? match : SPACING_PARAGRAPH_DEFAULT;
        },
        renderHTML: (attributes) => {
          const cls = attributes.class;
          const valid = typeof cls === "string" && SPACING_PARAGRAPH_VALID.test(cls);
          return {
            class: valid ? cls : SPACING_PARAGRAPH_DEFAULT,
          };
        },
      },
    };
  },
});

const Tiptap = React.forwardRef<
  HTMLDivElement,
  {
    description: string;
    label?: React.ReactNode;
    onChange: (richText: string) => void;
    disabled?: boolean;
    /** Panneau latéral étroit (ex. éditeur CMS avancé) : éditeur plus bas, toolbar compacte. */
    compact?: boolean;
    /** Barre d'outils réduite (lien seul) — cf. ToolBar.minimal. */
    minimal?: boolean;
    tooltip?: string;
    /**
     * Alignement par défaut appliqué aux paragraphes sans alignement explicite.
     * Ex. page de garde d'export → "center".
     */
    defaultTextAlign?: "left" | "center" | "right";
    /**
     * Preset d'outils : published | complete | compact.
     * Défaut complete (Notes / PDF) ; les formulaires passent resolveToolbarPreset(name).
     */
    toolbarPreset?: ToolbarPreset;
    /**
     * @deprecated Dérivé de toolbarPreset === "published". Conservé pour compat.
     */
    enableReadMore?: boolean;
  } & Omit<React.HTMLProps<HTMLDivElement>, "label">
>(
  (
    {
      description,
      onChange,
      label,
      disabled = false,
      compact = false,
      minimal = false,
      className,
      tooltip,
      defaultTextAlign,
      toolbarPreset = "complete",
      enableReadMore,
      ...props
    },
    ref,
  ) => {
    const resolvedPreset: ToolbarPreset =
      enableReadMore === true && toolbarPreset === "complete"
        ? "published"
        : toolbarPreset;

    const editor = useEditor({
      extensions: [
        StarterKit.configure({
          paragraph: false,
          heading: false,
        }),
        Indent,
        FontSize.configure({
          types: ["paragraph"],
          fontSize: [...CMS_FONT_SIZES_PX],
          defaultFontSize: "16px",
        }),
        TextStyle,
        CmsFontFamily,
        CmsFontColor,
        Underline,
        TextAlign.configure({
          types: ["heading", "paragraph"],
          defaultAlignment: defaultTextAlign,
        }),
        Link.configure({
          openOnClick: false,
          HTMLAttributes: {
            target: "_blank",
            rel: "noopener noreferrer",
          },
        }),
        CustomHeading.configure({
          levels: [2, 3],
        }),
        CustomParagraph,
        // Toujours enregistré pour préserver le marqueur du HTML existant, même
        // quand le bouton d'insertion est masqué.
        ReadMore,
      ],
      content: "",
      editable: !disabled,

      editorProps: {
        attributes: {
          class: cn(
            "w-full max-w-full rounded-md border bg-white text-black font-['Instrument Sans'] border-input bg-background focus:ring-offset-2 box-border",
            compact
              ? "min-h-[72px] max-h-[140px] overflow-y-auto p-1.5 text-sm leading-snug"
              : "min-h-[150px] p-2",
          ),
        },
      },
      onUpdate({ editor }) {
        onChange(editor.getHTML());
      },
    }, [compact, disabled, defaultTextAlign]);

    useEffect(() => {
      if (editor) {
        if (description !== editor.getHTML()) {
          editor.commands.setContent(description, false);
        }
      }
    }, [editor, description]);

    return (
      <div
        className={cn(
          "flex w-full min-w-0 flex-col justify-stretch text-start antialiased",
          disabled && "cursor-not-allowed",
          className,
        )}
        ref={ref}
        {...props}
      >
        {label ? (
          <div className="flex items-center gap-1.5">
            <Label
              className={`flex text-start text-slate-700 text-sm font-medium font-['Raleway'] leading-normal ${disabled ? "mb-2" : ""}`}
            >
              {label}
            </Label>
            {tooltip && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger
                    type="button"
                    className="bg-transparent p-0"
                    onClick={(e) => e.preventDefault()}
                  >
                    <HelpCircleIcon className="w-4 h-4 text-slate-500" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-black">{tooltip}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        ) : null}
        {!disabled && (
          <div className={cn("sticky top-0 z-10", compact ? "mb-1" : "")}>
            <ToolBar
              editor={editor}
              compact={compact}
              minimal={minimal}
              toolbarPreset={resolvedPreset}
            />
          </div>
        )}
        <EditorContent
          editor={editor}
          disabled={disabled}
          className={cn(
            "w-full",
            disabled && "opacity-50",
            "[&_.ProseMirror]:w-full [&_.ProseMirror]:max-w-none",
          )}
        />
      </div>
    );
  },
);

export default Tiptap;
